import { DefaultError, queryOptions, useQuery } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';
import { Workload } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const EXTERNAL_CLUSTER_REBALANCE_WORKLOADS_QUERY_KEY =
  'external-cluster-rebalance-workloads';

const getRebalanceWorkloadsQueryKey = (
  clusterId?: string,
  tourStepId?: string | number | null
) =>
  [
    EXTERNAL_CLUSTER_REBALANCE_WORKLOADS_QUERY_KEY,
    clusterId,
    tourStepId,
  ] as const;

const getRebalanceWorkloadsQueryOptions = ({
  clusterId,
  tourStepId,
  enabled = true,
  refetchInterval,
}: {
  clusterId?: string;
  tourStepId?: string | number | null;
  enabled?: boolean;
  refetchInterval?: number | false;
}) =>
  queryOptions({
    queryKey: getRebalanceWorkloadsQueryKey(clusterId, tourStepId),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getWorkloads(
        { clusterId: clusterId! },
        {
          timeout: 60_000,
        }
      );
      return data.workloads;
    },
    enabled: enabled && !!clusterId,
    refetchInterval,
  });

export type UseRebalanceWorkloadsQueryArgs = {
  clusterId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
};

export type UseRebalanceWorkloadsQuery = {
  isLoading: boolean;
  workloads?: Workload[];
  error?: DefaultError;
  refetch: () => void;
};

export const useRebalanceWorkloadsQuery = ({
  clusterId,
  enabled = true,
  refetchInterval,
}: UseRebalanceWorkloadsQueryArgs): UseRebalanceWorkloadsQuery => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);
  const {
    isLoading,
    data: workloads,
    error,
    refetch,
  } = useQuery(
    getRebalanceWorkloadsQueryOptions({
      clusterId,
      tourStepId,
      enabled,
      refetchInterval,
    })
  );

  return {
    isLoading,
    workloads,
    error: error || undefined,
    refetch,
  };
};
